import type { QueryClient, QueryKey } from "@tanstack/react-query";

/**
 * What the home screen last read, kept on the device so the next opening
 * shows it at once, then refreshes it behind. Without it every cold start
 * waited on three round trips over the tailnet with a blank page.
 *
 * Only what the home screen reads, and never a session under way: a set
 * count gone stale mid-set is worse than waiting for the server. A new
 * build starts empty, so a payload of the old shape never reaches new code.
 */

type Store = Pick<Storage, "getItem" | "setItem" | "removeItem">;

interface Kept {
  key: QueryKey;
  data: unknown;
}

interface Snapshot {
  build: string;
  savedAt: number;
  queries: Kept[];
}

const STORAGE_KEY = "home-cache";
const MAX_AGE_MS = 7 * 86_400_000;
// Several queries settle within one opening: one write for all of them.
const SAVE_AFTER_MS = 1000;

function storage(): Store | null {
  try {
    return window.localStorage;
  } catch {
    return null;
  }
}

const isObject = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

/** Whether a query's answer is one the home screen may show before the server's. */
export function keeps(key: QueryKey, data: unknown): boolean {
  const [head, second] = key;
  if (head === "block" || head === "cycle" || head === "account") return key.length === 1;
  if (head === "records" || head === "sessions") return key.length === 2 && second === "recent";
  if (head === "journal") return key.length === 2 && second === null;
  if (head === "wearable") return key.length === 2 && typeof second === "string";
  if (head === "insights") return key.length === 2 && second === "home";
  // The last session's card: a session over and done no longer moves.
  if (head === "session" && typeof second === "number" && key.length === 2) {
    return isObject(data) && isObject(data.session) && data.session.status === "done";
  }
  return false;
}

/**
 * Put back what the last opening kept, as stale whatever its age: a write
 * since may have outdated it without it being saved again — a session
 * rated, then the journal reopened, showed the old rating for good.
 */
export function restoreHome(
  client: QueryClient,
  build: string,
  now = Date.now(),
  store: Store | null = storage(),
): void {
  let snapshot: Snapshot;
  try {
    const raw = store?.getItem(STORAGE_KEY);
    if (!raw) return;
    snapshot = JSON.parse(raw) as Snapshot;
  } catch {
    return;
  }
  if (snapshot.build !== build || now - snapshot.savedAt > MAX_AGE_MS || !Array.isArray(snapshot.queries)) return;
  for (const { key, data } of snapshot.queries) {
    if (!Array.isArray(key) || !keeps(key, data)) continue;
    client.setQueryData(key, data);
    client.getQueryCache().find({ queryKey: key, exact: true })?.invalidate();
  }
}

export function saveHome(client: QueryClient, build: string, now = Date.now(), store: Store | null = storage()): void {
  const queries = client
    .getQueryCache()
    .getAll()
    .filter((query) => query.state.status === "success" && keeps(query.queryKey, query.state.data))
    .map((query) => ({ key: query.queryKey, data: query.state.data }));
  const snapshot: Snapshot = { build, savedAt: now, queries };
  try {
    store?.setItem(STORAGE_KEY, JSON.stringify(snapshot));
  } catch {
    /* full or refused: the next opening waits for the server, as before */
  }
}

/** Keep the snapshot current: written a moment after answers come in, not on a timer. */
export function watchHome(client: QueryClient, build: string): () => void {
  let timer: ReturnType<typeof setTimeout> | null = null;
  return client.getQueryCache().subscribe((event) => {
    if (event.type !== "updated" || event.action.type !== "success") return;
    if (!keeps(event.query.queryKey, event.query.state.data)) return;
    if (timer !== null) clearTimeout(timer);
    timer = setTimeout(() => {
      timer = null;
      saveHome(client, build);
    }, SAVE_AFTER_MS);
  });
}

/** Signed out: the next one on this device must not see this account's screen. */
export function forgetHome(store: Store | null = storage()): void {
  try {
    store?.removeItem(STORAGE_KEY);
  } catch {
    /* nothing kept that could be read either */
  }
}
